import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {ConfigService} from '@config/config.service';
import {TranslateService} from '@ngx-translate/core';
import {UsersService} from '@shared/services/users.service';
import {SharedService} from '@shared/shared.service';
import {ToastrService} from 'ngx-toastr';
import {BehaviorSubject, catchError, EMPTY, Subject} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {UserInfoModel} from "@services/back-office/model/userInfoModel";
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

@Component({
    selector: 'app-crm-global-search-dialog',
    templateUrl: './crm-global-search-dialog.component.html',
    styleUrls: ['./crm-global-search-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CrmGlobalSearchDialogComponent implements OnInit, OnDestroy {
  /** Subject to destroy Subscriptions */
  private destroyed$ = new Subject<void>();
  /** Loading */
  loading$ = new BehaviorSubject(false);
  /** Form */
  searchForm: UntypedFormGroup;
  searchResult: UserInfoModel;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      number?: string
    },
    public dialogRef: MatDialogRef<CrmGlobalSearchDialogComponent>,
    private config: ConfigService,
    private formBuilder: UntypedFormBuilder,
    private usersService: UsersService,
    private translateService: TranslateService,
    private toastrService: ToastrService,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    if (!this.searchForm) {
      this.initForm();
    }

    if (this.data.number) {
      this.onInputPaste(this.data.number, true);
    }
  }

  ngOnDestroy() {
    this.loading$.complete();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /** Init The Form */
  initForm() {
    this.searchForm = this.formBuilder.group({
      phone: ['', [Validators.required, Validators.pattern(this.config.regex.phone)]],
    });
  }

  /** Search user by phone number */
  search() {
    const userPhone = this.searchForm.get('phone')?.value;
    if (userPhone) {
      this.usersService.findUser({phoneNumber: `+38${userPhone}`}).pipe(
        take(1),
        map((response: UserInfoModel) => {
          this.searchResult = response;
          this.changeDetectorRef.detectChanges();
        }),
        catchError(() => {
          this.loading$.next(false);
          SharedService.showWarningWithTitle(
            this.translateService,
            this.toastrService,
            'modules.references.administrators.noUserFound'
          );
          return EMPTY;
        })
      ).subscribe();
    }
  }

  /** Take only last 10 digits from the phone clipboard data on Ctrl+v event */
  onInputPaste(clipboardData: string | undefined, forceSearch?: boolean) {
    if (clipboardData) {
      const hasNumberReg = /\d/;
      const hasNumber = hasNumberReg.test(clipboardData);
      if (hasNumber) {
        const trimmedValue = clipboardData.replace(/\D/g, '');
        let tenDigitsFromTheEnd = trimmedValue.slice(-10);

        if (tenDigitsFromTheEnd && tenDigitsFromTheEnd.length === 9) {
          tenDigitsFromTheEnd = `0${tenDigitsFromTheEnd}`;
        }

        if (tenDigitsFromTheEnd && tenDigitsFromTheEnd.length === 10) {
          this.searchForm.patchValue({
            phone: tenDigitsFromTheEnd
          });
        }

        if (forceSearch) {
          this.search();
        }
      }
    }
  }

  /** Close dialog */
  closeDialog(result: boolean, data?: any) {
    this.dialogRef.close({result, data});
  }

}
